import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ticket } from "lucide-react";

const empty = {
    code: "",
    type: "percent",
    value: 10,
    min_subtotal: 0,
    max_discount: null,
    usage_limit: null,
    valid_from: "",
    valid_until: "",
    is_active: true,
    applies_to: "all",
};

function CouponEditor({ open, onOpenChange, initial, onSaved }) {
    const isEdit = Boolean(initial?.id);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setForm(initial ? { ...empty, ...initial } : empty);
    }, [open, initial]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({
            ...f,
            [name]: type === "checkbox" ? checked
                : (["value", "min_subtotal", "max_discount", "usage_limit"].includes(name))
                    ? (value === "" ? null : Number(value))
                    : value,
        }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                code: (form.code || "").toUpperCase().trim(),
                valid_from: form.valid_from || null,
                valid_until: form.valid_until || null,
            };
            if (isEdit) {
                await api.patch(`/admin/coupons/${initial.id}`, payload);
                toast.success("Coupon updated");
            } else {
                await api.post("/admin/coupons", payload);
                toast.success("Coupon created");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="coupon-editor-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        {isEdit ? "Edit coupon" : "New coupon"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Code</Label>
                            <Input name="code" value={form.code} onChange={onChange} required placeholder="WELCOME10" className="mt-1.5 rounded-xl uppercase" data-testid="coupon-form-code" />
                        </div>
                        <div>
                            <Label>Type</Label>
                            <select name="type" value={form.type} onChange={onChange} className="mt-1.5 rounded-xl border border-input h-10 w-full px-3 text-sm bg-background" data-testid="coupon-form-type">
                                <option value="percent">Percent (%)</option>
                                <option value="flat">Flat (₹)</option>
                            </select>
                        </div>
                        <div>
                            <Label>Value {form.type === "percent" ? "(%)" : "(₹)"}</Label>
                            <Input type="number" name="value" min={1} value={form.value} onChange={onChange} required className="mt-1.5 rounded-xl" data-testid="coupon-form-value" />
                        </div>
                        <div>
                            <Label>Applies to</Label>
                            <select name="applies_to" value={form.applies_to} onChange={onChange} className="mt-1.5 rounded-xl border border-input h-10 w-full px-3 text-sm bg-background">
                                <option value="all">All items</option>
                                <option value="products">Products only</option>
                                <option value="courses">Courses only</option>
                            </select>
                        </div>
                        <div>
                            <Label>Min subtotal (₹)</Label>
                            <Input type="number" name="min_subtotal" min={0} value={form.min_subtotal ?? 0} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>Max discount cap (₹, optional)</Label>
                            <Input type="number" name="max_discount" min={0} value={form.max_discount ?? ""} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>Usage limit (total uses, optional)</Label>
                            <Input type="number" name="usage_limit" min={1} value={form.usage_limit ?? ""} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>Valid from</Label>
                            <Input type="datetime-local" name="valid_from" value={form.valid_from || ""} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>Valid until</Label>
                            <Input type="datetime-local" name="valid_until" value={form.valid_until || ""} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" name="is_active" checked={form.is_active} onChange={onChange} />
                        Active
                    </label>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="coupon-form-save">
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Create coupon"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function CouponsTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const reload = () => api.get("/admin/coupons").then(({ data }) => setItems(data));
    useEffect(() => { reload(); }, []);

    const remove = async (id) => {
        if (!confirm("Delete this coupon?")) return;
        try {
            await api.delete(`/admin/coupons/${id}`);
            toast.success("Coupon deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="space-y-4" data-testid="coupons-tab">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="font-heading text-2xl">Coupons</h3>
                    <p className="text-sm text-ink-700">Promo codes — flat or percent off, with min order, expiry and usage limits.</p>
                </div>
                <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="coupon-new-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> New coupon
                </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((c) => (
                    <div key={c.id} className="rounded-2xl bg-white border border-gray-100 p-4" data-testid={`coupon-row-${c.id}`}>
                        <div className="flex items-start gap-3 justify-between">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <Ticket className="h-4 w-4 text-brand-600" />
                                    <span className="font-mono font-semibold tracking-wide">{c.code}</span>
                                    {!c.is_active && <span className="text-[10px] uppercase bg-gray-100 text-ink-700 rounded-full px-2 py-0.5">inactive</span>}
                                </div>
                                <div className="text-sm text-ink-700">
                                    {c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`}
                                    {c.min_subtotal > 0 && <span> · min ₹{c.min_subtotal}</span>}
                                    {c.max_discount && <span> · max ₹{c.max_discount}</span>}
                                </div>
                                <div className="text-xs text-ink-700 mt-1">
                                    {c.applies_to === "all" ? "All items" : c.applies_to === "courses" ? "Courses only" : "Products only"}
                                    {" · "}{c.used_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ""} used
                                </div>
                            </div>
                            <div className="flex gap-1.5 shrink-0">
                                <Button size="icon" variant="outline" className="rounded-full h-8 w-8" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`coupon-edit-${c.id}`}>
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="outline" className="rounded-full h-8 w-8 text-accent-700 hover:bg-accent-50" onClick={() => remove(c.id)} data-testid={`coupon-delete-${c.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="col-span-full text-center py-10 text-ink-700">No coupons yet.</div>
                )}
            </div>
            <CouponEditor open={open} onOpenChange={setOpen} initial={editing} onSaved={reload} />
        </div>
    );
}
